import React, { useState, useCallback, useRef } from 'react';
import { useMap } from '../map/MapContext';
import { useOgcServices } from '../services/OgcServicesContext';
import { GPKG_COLLECTION_AIRPORTS } from '../config/environment';
import { buildAirportFilter, type AirportFilterCriteria } from '../services/cql2-aviation-presets';
import { and, or, ilike } from '../services/cql2-filter-builder';
import type { Feature, FeatureCollection } from 'geojson';
import type { ResolvedWaypoint } from '@efb/shared-core';
import { AirportInfoPanel } from './AirportInfoPanel';

interface AirportResult {
  id: string;
  ident: string;
  name: string;
  type: string;
  latitude: number;
  longitude: number;
  elevation?: number;
  city?: string;
  state?: string;
}

function featureToAirport(f: Feature): AirportResult {
  const p = f.properties ?? {};
  const coords = (f.geometry as any)?.coordinates ?? [0, 0];
  const ident = p.ident ?? p.icao_id ?? p.ICAO_ID ?? p.faa_id ?? p.FAA_ID ?? p.IDENT ?? '';
  return {
    id: f.id as string ?? p.id ?? '',
    ident,
    name: p.name ?? p.NAME ?? p.facility_name ?? p.arpt_name ?? p.DESIGNATOR ?? ident,
    type: p.type ?? p.facility_type ?? p.TYPE_CODE ?? p.BCN_TYPE ?? 'AIRPORT',
    latitude: coords[1],
    longitude: coords[0],
    elevation: p.elevation_ft ?? p.elev ?? p.ELEVATION,
    city: p.city ?? p.associated_city ?? p.CITY ?? p.SERVCITY,
    state: p.state ?? p.state_code ?? p.STATE,
  };
}

export function airportResultToWaypoint(airport: AirportResult): ResolvedWaypoint {
  return {
    identifier: airport.ident,
    name: airport.name,
    type: 'airport',
    latitude: airport.latitude,
    longitude: airport.longitude,
    elevation: airport.elevation,
    source: 'nasr',
  };
}

function isAirportBeaconCollection(): boolean {
  return GPKG_COLLECTION_AIRPORTS.toLowerCase().endsWith('.am_beacon_pending');
}

function isAirportFeatureCollection(): boolean {
  return GPKG_COLLECTION_AIRPORTS.toLowerCase().endsWith('.feat_airports');
}

// ─── Filter criteria defaults ────────────────────────────────────────────────

const AIRPORT_TYPES = ['AIRPORT', 'HELIPORT', 'SEAPLANE BASE', 'ULTRALIGHT', 'GLIDERPORT', 'BALLOONPORT'] as const;
const FUEL_TYPES = ['100LL', 'JET-A', 'MOGAS', 'JET-A+', '100'] as const;
const SURFACE_TYPES = ['ASPH', 'CONC', 'TURF', 'GRVL', 'DIRT', 'WATER'] as const;

export const AirportSearchPanel: React.FC = () => {
  const { map } = useMap();
  const { featuresClient } = useOgcServices();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AirportResult[]>([]);
  const [selected, setSelected] = useState<AirportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(0);
  const [showFilters, setShowFilters] = useState(false);

  // CQL filter criteria state
  const [filterTypes, setFilterTypes] = useState<string[]>([]);
  const [filterTowered, setFilterTowered] = useState<boolean | undefined>(undefined);
  const [filterFuel, setFilterFuel] = useState(false);
  const [filterFuelTypes, setFilterFuelTypes] = useState<string[]>([]);
  const [filterMinRunway, setFilterMinRunway] = useState('');
  const [filterSurfaces, setFilterSurfaces] = useState<string[]>([]);
  const [filterLighted, setFilterLighted] = useState(false);
  const [filterInstrument, setFilterInstrument] = useState(false);

  const buildCurrentCql = useCallback((): string => {
    const criteria: AirportFilterCriteria = {};
    if (filterTypes.length > 0) criteria.types = filterTypes;
    if (filterTowered !== undefined) criteria.towered = filterTowered;
    if (filterFuel) criteria.fuel = true;
    if (filterFuelTypes.length > 0) criteria.fuelTypes = filterFuelTypes;
    if (filterMinRunway) criteria.minRunwayLengthFt = parseInt(filterMinRunway, 10) || undefined;
    if (filterSurfaces.length > 0) criteria.runwaySurfaces = filterSurfaces;
    if (filterLighted) criteria.lighted = true;
    if (filterInstrument) criteria.instrumentApproach = true;
    return buildAirportFilter(criteria);
  }, [filterTypes, filterTowered, filterFuel, filterFuelTypes, filterMinRunway, filterSurfaces, filterLighted, filterInstrument]);

  const doSearch = useCallback(async () => {
    if (!query.trim()) return;
    const id = ++searchRef.current;
    setLoading(true);
    try {
      // Build the combined CQL: text search + facility filters
      const q = query.trim();
      const textCql = isAirportBeaconCollection()
        ? or(
            ilike('ICAO_ID', `%${q}%`),
            ilike('FAA_ID', `%${q}%`),
            ilike('DESIGNATOR', `%${q}%`),
          )
        : isAirportFeatureCollection()
          ? or(
              ilike('ICAO_ID', `%${q}%`),
              ilike('IDENT', `%${q}%`),
              ilike('NAME', `%${q}%`),
              ilike('SERVCITY', `%${q}%`),
            )
        : or(
            ilike('name', `%${q}%`),
            ilike('ident', `%${q}%`),
            ilike('city', `%${q}%`),
          );
      const facilityCql = buildCurrentCql();
      const combinedCql = facilityCql && !isAirportBeaconCollection()
        ? and(textCql, facilityCql)
        : textCql;

      const fc: FeatureCollection = await featuresClient.getFeatures('airports', {
        filter: combinedCql,
        limit: 50,
      });
      if (id !== searchRef.current) return; // stale
      setResults(fc.features.map(featureToAirport));
      setSelected(null);
    } catch (err) {
      console.error('Airport search failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query, featuresClient, buildCurrentCql]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') doSearch();
  }, [doSearch]);

  const flyTo = useCallback((apt: AirportResult) => {
    setSelected(apt);
    if (map) {
      map.flyTo({ center: [apt.longitude, apt.latitude], zoom: 12, duration: 1500 });
    }
  }, [map]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setSelected(null);
  }, []);

  const toggleChip = useCallback((value: string, list: string[], setter: (v: string[]) => void) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  }, []);

  // ─── Detail view ───────────────────────────────────────────────────────────
  if (selected) {
    return (
      <AirportInfoPanel
        airport={airportResultToWaypoint(selected)}
        onClose={() => setSelected(null)}
      />
    );
  }

  const activeFilterCount = [filterTypes.length > 0, filterTowered !== undefined, filterFuel, filterFuelTypes.length > 0,
    !!filterMinRunway, filterSurfaces.length > 0, filterLighted, filterInstrument].filter(Boolean).length;

  // ─── Search view ───────────────────────────────────────────────────────────
  return (
    <div>
      <div className="panel-section" style={{ display: 'flex', gap: 6 }}>
        <input
          className="input"
          placeholder="Search airports (ICAO, name, city…)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button className="btn btn-primary" onClick={doSearch} disabled={loading}>
          {loading ? '…' : '🔍'}
        </button>
        {results.length > 0 && (
          <button className="btn-icon" onClick={clearSearch} title="Clear">✕</button>
        )}
      </div>

      {/* CQL Facility Filters */}
      <div className="panel-section">
        <div className="collapsible-header" onClick={() => setShowFilters((p) => !p)}>
          <span className={`chevron ${showFilters ? 'open' : ''}`}>▶</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Facility Filters {activeFilterCount > 0 && <span style={{ color: 'var(--accent-light)' }}>({activeFilterCount} active)</span>}
          </span>
        </div>
        {showFilters && (
          <div className="collapsible-body" style={{ paddingTop: 6 }}>
            {/* Airport type */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>Facility Type</div>
              <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {AIRPORT_TYPES.map((t) => (
                  <button key={t} className={`chip ${filterTypes.includes(t) ? 'active' : ''}`}
                    onClick={() => toggleChip(t, filterTypes, setFilterTypes)} style={{ fontSize: 10 }}>{t}</button>
                ))}
              </div>
            </div>
            {/* Towered */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>Control Tower</div>
              <div style={{ display: 'flex', gap: 3 }}>
                {[{ label: 'Any', val: undefined }, { label: 'Towered', val: true }, { label: 'Non-towered', val: false }].map(({ label, val }) => (
                  <button key={label} className={`chip ${filterTowered === val ? 'active' : ''}`}
                    onClick={() => setFilterTowered(val as boolean | undefined)} style={{ fontSize: 10 }}>{label}</button>
                ))}
              </div>
            </div>
            {/* Fuel */}
            <div style={{ marginBottom: 8 }}>
              <label className="toggle" style={{ marginBottom: 4 }}>
                <span className={`toggle-box ${filterFuel ? 'checked' : ''}`} onClick={() => setFilterFuel(!filterFuel)} />
                <span style={{ fontSize: 12 }}>Has Fuel</span>
              </label>
              {filterFuel && (
                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', paddingLeft: 26 }}>
                  {FUEL_TYPES.map((f) => (
                    <button key={f} className={`chip ${filterFuelTypes.includes(f) ? 'active' : ''}`}
                      onClick={() => toggleChip(f, filterFuelTypes, setFilterFuelTypes)} style={{ fontSize: 10 }}>{f}</button>
                  ))}
                </div>
              )}
            </div>
            {/* Min runway */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>Min Runway Length (ft)</div>
              <input className="input" type="number" placeholder="e.g. 3000"
                value={filterMinRunway} onChange={(e) => setFilterMinRunway(e.target.value)}
                style={{ width: 120 }} />
            </div>
            {/* Surface types */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>Runway Surface</div>
              <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {SURFACE_TYPES.map((s) => (
                  <button key={s} className={`chip ${filterSurfaces.includes(s) ? 'active' : ''}`}
                    onClick={() => toggleChip(s, filterSurfaces, setFilterSurfaces)} style={{ fontSize: 10 }}>{s}</button>
                ))}
              </div>
            </div>
            {/* Lighted + Instrument */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
              <label className="toggle">
                <span className={`toggle-box ${filterLighted ? 'checked' : ''}`} onClick={() => setFilterLighted(!filterLighted)} />
                <span style={{ fontSize: 12 }}>Lighted</span>
              </label>
              <label className="toggle">
                <span className={`toggle-box ${filterInstrument ? 'checked' : ''}`} onClick={() => setFilterInstrument(!filterInstrument)} />
                <span style={{ fontSize: 12 }}>Instrument Approach</span>
              </label>
            </div>
            {/* Active CQL preview */}
            {activeFilterCount > 0 && (
              <div style={{ background: 'var(--bg-input)', borderRadius: 4, padding: 6, marginTop: 4 }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>CQL2 Filter:</div>
                <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--accent-light)', wordBreak: 'break-all' }}>
                  {buildCurrentCql()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {results.length > 0 && (
        <div className="panel-section">
          <div className="panel-section-title">{results.length} Results</div>
          {results.map((apt) => (
            <div key={apt.id || apt.ident} className="list-item" onClick={() => flyTo(apt)}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{apt.ident}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {apt.name}{apt.city ? ` — ${apt.city}` : ''}
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}>
                {apt.elevation != null ? `${apt.elevation}'` : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      {results.length === 0 && !loading && query && (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 20 }}>
          Press 🔍 or Enter to search
        </div>
      )}
    </div>
  );
};
